import { useCallback } from 'react';
import { useParams } from 'react-router-dom';

/**
 * Build an absolute in-app path under a workspace slug (spec 0013, ADR-0003).
 * The whole authenticated SPA lives under `/:workspaceSlug`, so every internal
 * link must carry that prefix.
 *
 *   wsPath('acme')                        -> '/acme'
 *   wsPath('acme', 'agents')              -> '/acme/agents'
 *   wsPath('acme', 'agents', 'support')   -> '/acme/agents/support'
 *
 * Query strings are appended by the caller: `${wsPath(...)}?session=<id>`.
 */
export function wsPath(slug: string, ...segments: (string | number)[]): string {
  const tail = segments
    .flatMap((s) => String(s).split('/'))
    .map((s) => s.trim())
    .filter(Boolean)
    .join('/');
  return tail ? `/${slug}/${tail}` : `/${slug}`;
}

/**
 * Returns a builder bound to the current route's `:workspaceSlug`. Use inside
 * any component rendered under the workspace layout to build workspace-relative
 * links without repeating the slug.
 */
export function useWorkspacePath(): (...segments: (string | number)[]) => string {
  const { workspaceSlug } = useParams<{ workspaceSlug: string }>();
  return useCallback(
    (...segments: (string | number)[]) => wsPath(workspaceSlug ?? '', ...segments),
    [workspaceSlug],
  );
}
